import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Newspaper, Users, IdCard, FolderTree, Eye, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

const StatCard = ({ label, value, icon: Icon, hint }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <Icon className="w-4 h-4 text-slate-400"/>
    </div>
    <div className="text-3xl font-bold text-[#0F172A]">{value}</div>
    {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
  </div>
);

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <div className="text-slate-500">लोड हो रहा है…</div>;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">डैशबोर्ड</h1>
          <p className="text-sm text-slate-500 mt-1">आपके समाचार पोर्टल का सारांश</p>
        </div>
        <div className="text-xs text-slate-500">{new Date().toLocaleDateString("hi-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="कुल समाचार" value={stats.news_count} icon={Newspaper} hint={`${stats.published_count} प्रकाशित`}/>
        <StatCard label="रिटेलर" value={stats.retailer_count} icon={Users}/>
        <StatCard label="स्टाफ" value={stats.staff_count} icon={IdCard}/>
        <StatCard label="कुल व्यू" value={stats.total_views} icon={Eye}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">पिछले 7 दिन</div>
              <div className="text-lg font-bold text-[#0F172A]">प्रकाशित समाचार</div>
            </div>
            <TrendingUp className="w-4 h-4 text-[#2563EB]"/>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={stats.last_7_days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="day" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }}/>
              <Tooltip/>
              <Line type="monotone" dataKey="count" stroke="#1E3A8A" strokeWidth={2} dot={{ r: 4 }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1">श्रेणियाँ</div>
          <div className="text-lg font-bold text-[#0F172A] mb-4">प्रति श्रेणी समाचार</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.by_category} layout="vertical">
              <XAxis type="number" tick={{ fontSize: 11 }}/>
              <YAxis type="category" dataKey="category" tick={{ fontSize: 11 }} width={90}/>
              <Tooltip/>
              <Bar dataKey="count" fill="#2563EB" radius={[0, 4, 4, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
